import type { Seg, Span } from '../playback/ranges';

/** Quick replays offered in the mosaic window, in minutes. */
export const REPLAY_MINUTES = [1, 3, 5, 10, 15] as const;
/** Replay speeds: every tile decodes at once, so faster ones are left out. */
export const RATES = [1, 2, 4] as const;
export type Rate = (typeof RATES)[number];

/** A tile further than this from the shared clock (in recording seconds) seeks. */
export const DRIFT_S = 1;
/** Replay returns to live once the clock is this close to now. */
export const LIVE_EDGE_MS = 2000;

const HOUR = 3_600_000;

/**
 * The shared replay clock: `base` is the recording instant at the moment
 * `at` (performance.now()), advancing at `rate` while playing.
 */
export interface Clock {
  base: number;
  at: number;
  rate: Rate;
  playing: boolean;
}

export function startClock(t: number, perf: number): Clock {
  return { base: t, at: perf, rate: 1, playing: true };
}

/** The recording instant the clock shows at `perf`. */
export function clockTime(c: Clock, perf: number): number {
  return c.playing ? c.base + (perf - c.at) * c.rate : c.base;
}

/** The clock changed at `perf` (seek, pause, speed), keeping time continuous. */
export function rebase(c: Clock, perf: number, patch: Partial<Omit<Clock, 'at'>>): Clock {
  return { ...c, base: clockTime(c, perf), ...patch, at: perf };
}

/**
 * Where a tile plays instant t: the segment covering it. Unlike the single
 * playback page there is no jump over gaps — a camera without recording at t
 * shows nothing while the others keep playing.
 */
export function segmentAt(segs: Seg[], t: number): { seg: Seg; offsetMs: number } | null {
  for (const s of segs) {
    if (t >= s.start && t < s.end) return { seg: s, offsetMs: t - s.start };
  }
  return null;
}

/**
 * Hour windows of recordings a tile loads around t: its own hour, and the
 * next one in the last two minutes so the switch has no wait.
 */
export function windowsAt(t: number): number[] {
  const h = Math.floor(t / HOUR) * HOUR;
  return t - h >= HOUR - 2 * 60_000 ? [h, h + HOUR] : [h];
}

export const windowEnd = (w: number) => w + HOUR;

/** Segments of several windows, without the ones listed twice, by start. */
export function mergeSegs(lists: Seg[][]): Seg[] {
  const byId = new Map<string, Seg>();
  for (const l of lists) for (const s of l) byId.set(s.id, s);
  return [...byId.values()].sort((a, b) => a.start - b.start);
}

/** Where the replay reads recordings: the platform's files or the device's own. */
export type ReplaySource = 'server' | 'device';

/**
 * The instant a tile's device stream starts from: the device plays only
 * forward at normal speed, so a stream opens at the last discontinuity of the
 * clock (`epoch`: start, seek or resume) or, when the clock entered a recorded
 * span later, at the start of that span. Null in a gap. Whole seconds keep the
 * session stable across ticks.
 */
export function deviceStart(spans: Span[], t: number, epoch: number): number | null {
  const span = spans.find((s) => t >= s.start && t < s.end);
  if (!span) return null;
  return Math.floor(Math.max(epoch, span.start) / 1000) * 1000;
}

/**
 * Recorded spans of several cameras as one track: an instant is covered when
 * at least one camera recorded it.
 */
export function unionSpans(lists: Span[][]): Span[] {
  const all = lists
    .flat()
    .filter((s) => s.end > s.start)
    .sort((a, b) => a.start - b.start);
  const out: Span[] = [];
  for (const s of all) {
    const last = out.at(-1);
    if (last && s.start <= last.end) last.end = Math.max(last.end, s.end);
    else out.push({ start: s.start, end: s.end });
  }
  return out;
}

/**
 * Where a click on a track plays from: t itself when recorded, or else the
 * start of the next recording. Null after the last one.
 */
export function nextRecorded(spans: Span[], t: number): number | null {
  for (const s of spans) {
    if (t < s.start) return s.start;
    if (t < s.end) return t;
  }
  return null;
}
